import { ShopListInterface, ShopType } from '../interfaces/shop-list-interface';
import { OfferInterface } from '../interfaces/offer-interface';
import { LocationInterface } from '../interfaces/location-interface';
import { BusinessHoursInterface } from '../interfaces/business-hours-interface';
import { DiscountCountdownInterface } from '../interfaces/discount-countdown-interface';
import { DiscountPromotionsInterface } from '../interfaces/discount-promotions-interface';
import { ShopRatingInterface } from '../interfaces/shop-rating-interface';
import { SpecialBusinessHourInterface } from '../interfaces/special-business-hour-interface';
import { BookingPauseInterface } from '../interfaces/booking-pause-interface';
import { AmountInterface } from '../../layout/interfaces/amount-interface';
import { violatesMaxDuration, violatesMinDelay, violatesMinDuration } from './violationCheck';
import { BookingParametersInterface } from './bookingParameters';

export enum unbookableType {
  before = 'beforeSeason', // try to book before shop's season
  after = 'afterSeason', // try to book after shop's  season
  pause = 'pause', // there is a product on pause in the cart
  minDelay = 'minimumDelay', // (day-based) it's too late to book
  negativeMinDelay = 'negativeMinimumDelay', // these are for the same-day booking (hours-based)
  minDuration = 'minimumDuration', // booking is too short (days)
  maxDuration = 'maximumDuration', // boooking is too long (days)
  openingHours = 'openingHours', // TODO: checkOpeningHoursviolations
  unservableAges = 'unservableAges' // shop can't offer equipment for ages in search
}

const unbookableTypePriorities: Array<string> = [
  'beforeSeason',
  'afterSeason',
  'pause',
  'minimumDelay',
  'negativeMinimumDelay',
  'minimumDuration',
  'maximumDuration',
  'openingHours',
  'unservableAges'
];

export interface UnbookableReason {
  type: unbookableType;
  start?: Date; // some violations have start and end date, bookingRestrictions have them always
  end?: Date;
  minDelay?: number;
  minDuration?: number;
  maxDuration?: number;
  age?: number;
}

export class Shop implements ShopListInterface, OfferInterface, DiscountCountdownInterface {
  private lastStartcheck: Date;
  private lastEndCheck: Date;

  ageIntervals: import('../interfaces/age-interval-interface').AgeIntervalInterface[];
  shopId: number;
  products: import('../interfaces/detailed-offer-interface').DetailedOfferInterface[];
  rating: ShopRatingInterface;
  rawProperties;
  isSensitive?: boolean;

  showOnMap = false;
  // other, not yet implemented
  // isBookable: boolean;
  unbookableReason: string;
  min_delay: number;
  min_duration: number;
  max_duration: number;
  pause_start: Date;
  pause_end: Date;
  booking_pauses: Array<BookingPauseInterface> = [];

  isOfferAvailable: boolean;

  // OfferInterface
  baseTurnover: number; // unused?
  baseTurnoverAmount: AmountInterface;
  currency: string;
  rates: { [key: string]: number };
  duration: number;
  hasMinOneStoppedProduct: boolean;

  // helper to prevent getters and function calls
  difference: number;
  seasonFromDate: Date;
  seasonToDate: Date;

  // DiscountCountdownInterface
  until: string;
  timestamp: string;
  showToLocals: boolean;
  excludeMerchantIds: string;
  excludeCountryCodes: string;
  hasDiscountCountdown: boolean;

  // ShopInterface
  id: string;
  isMicrosite: boolean;
  name: string;
  ageCategories: Array<{
    name: string;
    id: string;
    classNameMale?: string; // no longer in use
    classNameFemale?: string; // no longer in use
  }>;
  isTopShop: boolean;
  isVirtualShop: boolean;
  isDiscountShop: boolean;
  hasGuaranty: boolean;
  hasModelChange: boolean;
  hasDepot: boolean;
  hasFreeChildren: boolean;
  address: string;
  requiredAdultsForFreeChildren: number;
  freeChildrenPerAdults: number;
  childrenFreeUntilAge: number;
  freeChildrenExplanation: string;
  shopSlug: string;
  townName: string;
  townId: number;
  townSlug: string;
  townSlugs: { [key: string]: string };
  location: LocationInterface;
  position: number;
  defaultProductDefinitionTitle: string;
  defaultProductBasePrice: string;
  defaultProductOnlinePrice: string;
  defaultProductOnlinePriceRaw: number;
  defaultProductOnlineAmount: AmountInterface;
  defaultProductBasePriceRaw: number;
  discount: number;
  imagePath: string;
  imagePathBig: string;
  panoramaImage: string;
  distance: number;
  properties: string[];
  additionalImagePaths: string[] = [];
  inTown: boolean;
  phone: string;
  email: string;
  companyId: number;
  companyShortName: string;
  merchantId: number;
  countryCode: string;
  companyLogoSmall: string;
  isFormerSport2000Company: boolean;
  isIntersportCompany: boolean;
  businessHours: Array<BusinessHoursInterface> = [];
  specialBusinessHours: Array<SpecialBusinessHourInterface> = [];
  pickupTime: string;
  discountPromotions: DiscountPromotionsInterface | [];
  minOnlineTurnoverFormatted: string;
  minOnlineTurnoverRaw: number;
  currentOnlineTurnoverRaw: number;
  minPersonCount: number;
  virtualShopParentId: number;
  virtualShopParentTown: string;
  vsEquipmentToLocation: boolean;
  vsLocationDropOff: boolean;
  seasonTo: string;
  seasonFrom: string;
  usesAgeIntervalLength: boolean;
  merchantName: string;
  companyName: string;
  isBindingMandatory: boolean;
  shopType: ShopType;
  hasDiscount = false; // used to prevent "save up to 0%!" badges and so on

  isHovered = false; // used in v2 of mapWithList test
  hotelDistance?: {
    hotel: google.maps.places.PlaceResult;
    distanceSeconds?: number; // travel distance in seconds
    distanceMeters?: number; // travel distance in meters (road)
    distanceSecondsCar?: number;
    distanceMetersCar?: number;
  }; // distance to Hotel in meters (calculated by sort hotel selection)

  // the actual offer for this shop
  offer: OfferInterface;

  unbookable_reasons = Array<UnbookableReason>();

  get imagePathBigWebp() {
    return this.imagePathBig.replace('jpg', 'webp');
  }

  constructor(shopList: ShopListInterface) {
    for (const property in shopList) {
      if (Object.prototype.hasOwnProperty.call(shopList, property)) {
        this[property] = shopList[property];
      }
    }
    if (this.booking_pauses) {
      this.booking_pauses.forEach(pause => {
        pause.startDate = new Date(pause.start);
        pause.endDate = new Date(pause.end);
      });
    }
    if (this.specialBusinessHours.length) {
      // format dates
      this.specialBusinessHours.forEach((specialBusinessHour: SpecialBusinessHourInterface) => {
        specialBusinessHour.start_date = new Date(specialBusinessHour.start);
        specialBusinessHour.end_date = new Date(specialBusinessHour.end);
      });
      this.specialBusinessHours.sort((a, b) => {
        if (a.start_date.getTime() > b.start_date.getTime()) {
          return 1;
        } else if (a.start_date.getTime() < b.start_date.getTime()) {
          return -1;
        }
        return 0;
      });
    }
    this.currentOnlineTurnoverRaw = this.minOnlineTurnoverRaw;

    this.isOfferAvailable = true;
    this.hasDiscountCountdown = false;
    this.distance = +this.distance;
    this.seasonFromDate = new Date(this.seasonFrom + 'T00:00:00.000');
    this.seasonToDate = new Date(this.seasonTo + 'T23:59:59.999');
  }

  /**
   * Updates Prices according to current offer, discounts and currency.
   * @param {OfferInterface} offer current offer provided by the api
   */
  updateOffer(offer: OfferInterface) {
    this.baseTurnoverAmount = offer.baseTurnoverAmount;
    this.duration = offer.duration;
    this.discount = offer.discount;

    this.offer = offer;
    this.hasMinOneStoppedProduct = offer.hasMinOneStoppedProduct;
    if (this.defaultProductBasePriceRaw === this.defaultProductOnlinePriceRaw) {
      // has no discount
      this.hasDiscount = false;
    } else {
      this.hasDiscount = true;
    }

    // update display prices
    // might cause problems later on?
    this.setPrices();
  }

  /**
   * Sets the default-product-base- and den default-product-online-price
   * to display in the "proceed to product list" button.
   */
  setPrices() {
    this.defaultProductBasePriceRaw = this.offer.baseTurnover; // * conversionFactor;
    this.defaultProductOnlinePriceRaw = this.defaultProductBasePriceRaw * (1 - this.discount);
    this.difference = this.defaultProductBasePriceRaw * this.discount;

    // work with real amounts
    this.defaultProductOnlineAmount = {
      amount: this.offer.baseTurnover * (1 - this.discount),
      currency: this.baseTurnoverAmount.currency
    };
  }

  /**
   * Recalculates the product-prices, based on the rates of the offer if exist
   */
  recalculateProductPrices() {
    if (this.offer) {
      this.setPrices();
    }
  }

  /**
   * updates the shops discount info
   */
  updateDiscountCountdown(discount: DiscountCountdownInterface) {
    if (new Date().getTime() / 1000 < +discount.timestamp) {
      this.hasDiscountCountdown = true;
      this.until = discount.until;
      this.timestamp = discount.timestamp;
      this.excludeMerchantIds = discount.excludeMerchantIds;
      this.excludeCountryCodes = discount.excludeCountryCodes;
      this.showToLocals = discount.showToLocals;
    }
  }

  getFullSlug(lang: string) {
    return `${this.townSlugs[lang]}/${this.shopSlug}`;
  }

  isDeliveringEquipmentToLocation(): boolean {
    return this.isVirtualShop && this.vsEquipmentToLocation;
  }

  /**
   * returns an array of all reasons why a booking for the given range is not possible
   * @param _start startDate of the booking
   * @param _end endDate of the booking
   */
  unbookableReasonsFor(_start: Date, _end: Date, _ages: Array<number> = []): Array<UnbookableReason> {
    const start = new Date(_start.getTime());
    const end = new Date(_end.getTime());
    start.setHours(0, 0, 0, 0); // set start and
    end.setHours(23, 59, 59, 999); // end to reflect a "day";

    // date hasn't changed? return checked unbookable_reasons;
    if (
      this.lastStartcheck &&
      this.lastEndCheck &&
      this.lastStartcheck.getTime() === _start.getTime() &&
      this.lastEndCheck.getTime() === _end.getTime()
    ) {
      return this.unbookable_reasons;
    } else {
      this.lastStartcheck = _start;
      this.lastEndCheck = _end;
    }

    // filter all relevantPauses for the given start/end date.
    const relevantPauses = this.booking_pauses.filter(pause => {
      pause.startDate.setHours(0, 0, 0, 0);
      pause.endDate.setHours(23, 59, 59, 999);
      // return start.getTime() >= pause.startDate.getTime() && start.getTime() <= pause.endDate.getTime();
      // you can't return equipment in a booking pause. we have to check the end date as well
      // [PAUSE {start}-> ENDPAUSE] <-{end}     <- no go
      // {start}-> [PAUSE <-{end} ENDPAUSE]     <- no go
      // {start}-> [PAUSE ENDPAUSE] <-{end}     <- ok!
      return (
        (start.getTime() >= pause.startDate.getTime() && start.getTime() <= pause.endDate.getTime()) ||
        (end.getTime() >= pause.startDate.getTime() && end.getTime() <= pause.endDate.getTime())
      );
    });

    this.unbookable_reasons = Array<UnbookableReason>();

    // check before_season
    if (start.getTime() < this.seasonFromDate.setHours(0, 0, 0, 0)) {
      this.unbookable_reasons.push({
        type: unbookableType.before,
        start: this.seasonFromDate,
        end: this.seasonToDate
      });
    }

    // check after_season
    if (end.getTime() > this.seasonToDate.setHours(23, 59, 59, 999)) {
      this.unbookable_reasons.push({
        type: unbookableType.after,
        start: this.seasonFromDate,
        end: this.seasonToDate
      });
    }

    // check pauses
    const pauses = relevantPauses.filter(pause => pause.type === 'stopSales');
    if (pauses.length) {
      pauses.forEach(pause => {
        this.unbookable_reasons.push({
          type: unbookableType.pause,
          start: pause.startDate,
          end: pause.endDate
        });
      });
    }

    // check ages
    for (let index = 0; index < _ages.length; index++) {
      const res = this.ageIntervals.find(
        interval =>
          (interval.start <= _ages[index] && interval.end >= _ages[index]) ||
          interval.height_end ||
          interval.height_start
      );
      if (!res) {
        this.unbookable_reasons.push({ type: unbookableType.unservableAges, age: _ages[index] });
        break;
      }
    }
    // foreach this.ages age find  this.ageIntervals where between start - end
    // if null result, add unbookable_reasons, break loop

    // check min_delay, max_duration, min_duration
    const modifiers = relevantPauses.filter(pause => pause.type === 'bookingRestriction');
    if (modifiers.length) {
      modifiers.forEach(modifier => {
        if (modifier.min_delay === null) {
          modifier.min_delay = this.min_delay;
        }
        if (modifier.min_duration === null) {
          modifier.min_duration = this.min_duration;
        }
        if (modifier.max_duration === null) {
          modifier.max_duration = this.max_duration;
        }

        // min_delay
        // are the hours from now until the start of the booking more than is minimum required?
        if (violatesMinDelay(start, modifier.min_delay)) {
          if (modifier.min_delay === 0) {
            // should be 1 day before
            this.unbookable_reasons.push({
              type: unbookableType.minDelay,
              minDelay: 1,
              start: modifier.startDate,
              end: modifier.endDate
            });
          }
          if (modifier.min_delay < 0) {
            // negative hours, same day but still too late
            this.unbookable_reasons.push({
              type: unbookableType.negativeMinDelay,
              start: modifier.startDate,
              end: modifier.endDate
            });
          }
          if (modifier.min_delay > 0) {
            this.unbookable_reasons.push({
              type: unbookableType.minDelay,
              minDelay: Math.ceil(modifier.min_delay / 24),
              start: modifier.startDate,
              end: modifier.endDate
            });
          }
        }

        // max_duration
        if (violatesMaxDuration(start, end, modifier.max_duration)) {
          this.unbookable_reasons.push({
            type: unbookableType.maxDuration,
            maxDuration: modifier.max_duration,
            start: modifier.startDate,
            end: modifier.endDate
          });
        }

        // min_duration
        if (violatesMinDuration(start, end, modifier.min_duration)) {
          this.unbookable_reasons.push({
            type: unbookableType.minDuration,
            minDuration: modifier.min_duration,
            start: modifier.startDate,
            end: modifier.endDate
          });
        }
      });
    } else {
      // default checks
      // min_delay
      // are the hours from now until the start of the booking more than is minimum required?
      if (violatesMinDelay(start, this.min_delay)) {
        if (this.min_delay === 0) {
          // should be 1 day before
          this.unbookable_reasons.push({
            type: unbookableType.minDelay,
            minDelay: 1
          });
        }
        if (this.min_delay < 0) {
          // negative hours, same day but still too late
          this.unbookable_reasons.push({
            type: unbookableType.negativeMinDelay
          });
        }
        if (this.min_delay > 0) {
          this.unbookable_reasons.push({
            type: unbookableType.minDelay,
            minDelay: Math.ceil(this.min_delay / 24)
          });
        }
      }
      // max_duration
      if (violatesMaxDuration(start, end, this.max_duration)) {
        this.unbookable_reasons.push({
          type: unbookableType.maxDuration,
          maxDuration: this.max_duration
        });
      }

      // min_duration
      if (violatesMinDuration(start, end, this.min_duration)) {
        this.unbookable_reasons.push({
          type: unbookableType.minDuration,
          minDuration: this.min_duration
        });
      }
    }
    return this.unbookable_reasons;
  }

  primaryUnbookableReasonFor(start: Date, end: Date, ages: Array<number> = []): UnbookableReason | null {
    const allReasons = this.unbookableReasonsFor(start, end, ages);

    const filteredReasons = [];
    unbookableTypePriorities.forEach(name => {
      const res = allReasons.find(reason => reason.type === name);
      if (res) {
        filteredReasons.push(res);
      }
    });
    if (filteredReasons.length > 0) {
      return filteredReasons[0];
    }

    return null;
  }

  getRelevantStopProductPauses(start: Date, end: Date): Array<BookingPauseInterface> {
    const relevantPauses = [];

    this.booking_pauses.forEach(bookingPause => {
      if (bookingPause.type === 'stopProduct') {
        const startPause = new Date(bookingPause.start);
        const endPause = new Date(bookingPause.end);

        if ((start >= startPause && start <= endPause) || (end >= startPause && end <= endPause)) {
          relevantPauses.push(bookingPause);
        }
      }
    });

    return relevantPauses;
  }

  isGroupDiscountPromotionActive(
    bookingParameters: BookingParametersInterface,
    discount: { type: string; criterion: number; percentage: number }
  ): boolean {
    if (Array.isArray(this.discountPromotions)) {
      return false;
    }
    let _discount: { type: string; criterion: number; percentage: number };
    for (let index = 0; index < this.discountPromotions.group.length; index++) {
      if (this.discountPromotions.group[index].criterion <= bookingParameters.ages.length) {
        _discount = this.discountPromotions.group[index];
      }
    }
    if (_discount?.criterion === discount.criterion && _discount?.percentage === discount.percentage) {
      // only for adults? .filter(age => age > 35)
      return true;
    }

    return false;
  }

  isFreeChildrenActive(bookingParameters: BookingParametersInterface): boolean {
    if (
      // meets minimum adult criteria AND has at least one child age that applies
      bookingParameters.ages.filter(a => a > 35).length >= this.requiredAdultsForFreeChildren &&
      bookingParameters.ages.filter(a => a <= this.childrenFreeUntilAge).length
    ) {
      return true;
    }
    return false;
  }
}
